/**
 * Browser-framed live preview of a public-site URL (owner 2026-09-07):
 * traffic lights + address bar, so it reads as "your site in a browser".
 *
 * Extracted from useAppearanceEditor (INT-0028) so the wizard finish screen
 * can show the same frame. Desktop renders the site at a REAL 1280px
 * viewport scaled down to the box (otherwise the public site shows its
 * mobile layout inside a ~450px iframe); mobile is a 390px column.
 *
 * Callers decide WHEN to mount it: below xl the finish screen renders
 * nothing instead of hiding it with CSS, so phones never load the iframe.
 */
import { useEffect, useState } from 'react';

export const DESKTOP_PREVIEW_W = 1280;

export interface SitePreviewFrameProps {
  src: string;
  mobile: boolean;
  title?: string;
  testId?: string;
}

export default function SitePreviewFrame({ src, mobile, title = 'Public site preview', testId = 'appearance-preview' }: SitePreviewFrameProps) {
  // CALLBACK ref, not a ref object: the box can mount LATE (once the org
  // resolves and src exists), so a mount-time effect never saw it and the
  // scale stuck at the fallback. The callback re-attaches the observer on
  // every (re)mount, including mobile/desktop toggles.
  const [boxEl, setBoxEl] = useState<HTMLDivElement | null>(null);
  const [boxW, setBoxW] = useState(0);
  useEffect(() => {
    if (!boxEl) return undefined;
    const measure = () => setBoxW(boxEl.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(boxEl);
    return () => ro.disconnect();
  }, [boxEl]);
  const scale = boxW > 0 ? boxW / DESKTOP_PREVIEW_W : 0.35;

  return (
    <div className={`${mobile ? 'mx-auto w-[390px] max-w-full' : 'w-full'} overflow-hidden rounded-xl border border-slate-400/80 shadow-lg shadow-slate-900/10 bg-white`}>
      <div className="flex items-center gap-2 border-b border-slate-300 bg-slate-100 px-3 py-2">
        <span className="flex gap-1.5" aria-hidden="true">
          <span className="h-3 w-3 rounded-full bg-[#ff5f57]" />
          <span className="h-3 w-3 rounded-full bg-[#febc2e]" />
          <span className="h-3 w-3 rounded-full bg-[#28c840]" />
        </span>
        <span className="ml-1 flex-1 truncate rounded-md bg-background border border-border px-3 py-1 text-xs text-muted-foreground">
          {src.replace(/^https?:\/\//, '')}
        </span>
      </div>
      {mobile ? (
        <iframe
          key={src}
          src={src}
          title={title}
          className="w-full bg-white"
          style={{ height: '70vh', border: 0 }}
          data-testid={testId}
        />
      ) : (
        <div
          ref={setBoxEl}
          className="w-full overflow-hidden bg-white"
          // Browser-like ratio (owner 2026-09-07): a 1280x800 viewport,
          // wider than tall, instead of a 70vh column.
          style={{ aspectRatio: '1280 / 800' }}
        >
          <iframe
            key={src}
            src={src}
            title={title}
            className="bg-white"
            style={{
              width: DESKTOP_PREVIEW_W,
              height: `${100 / scale}%`,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              border: 0,
            }}
            data-testid={testId}
          />
        </div>
      )}
    </div>
  );
}
